import React, {useMemo} from 'react';
import {
  AbsoluteFill,
  Audio,
  Freeze,
  OffthreadVideo,
  Sequence,
  staticFile,
  useCurrentFrame,
  useVideoConfig,
} from 'remotion';
import type {CalculateMetadataFunction} from 'remotion';
import {
  HighlightOverlay,
  FocusAreaMarker,
} from '../../../components/markers';
import {parseMedia} from '@remotion/media-parser';
import {webReader} from '@remotion/media-parser/web';
import type {HighlightItem} from '../video-highlight/types';
import {isHighlightActiveAtTime} from '../video-highlight/highlightTiming';
import type {
  CommentItem,
  TimelineHighlightItem,
  VideoCommentaryHighlightProps,
} from './types';

const DEFAULT_WIDTH = 1920;
const DEFAULT_HEIGHT = 1080;
const DEFAULT_FPS = 30;

const resolveMediaSrc = (src: string): string => {
  if (/^(https?:)?\/\//.test(src) || src.startsWith('data:')) {
    return src;
  }

  // If the path has already been resolved through `staticFile()`, avoid
  // wrapping it again. Double-resolving produces a broken `/static-*` URL.
  if (src.startsWith('/static-')) {
    return src;
  }

  if (src.startsWith('/public/') || src.startsWith('public/')) {
    const normalized = src.replace(/^\/?public\//, '');
    return staticFile(normalized);
  }

  if (src.startsWith('/')) {
    return staticFile(src.slice(1));
  }

  return src;
};

async function getAudioDuration(src: string): Promise<number> {
  const result = await parseMedia({
    src,
    fields: {durationInSeconds: true},
    reader: webReader,
  });

  return result.durationInSeconds ?? 0;
}

async function getVideoMeta(src: string): Promise<{
  duration: number;
  width: number;
  height: number;
  fps: number;
}> {
  const result = await parseMedia({
    src,
    fields: {durationInSeconds: true, dimensions: true, fps: true},
    reader: webReader,
  });

  return {
    duration: result.durationInSeconds ?? 0,
    width: result.dimensions?.width ?? 0,
    height: result.dimensions?.height ?? 0,
    fps: result.fps ?? 0,
  };
}

const getCommentAudioDuration = (comment: CommentItem): number => {
  return Math.max(0, comment.audioEndTime - comment.audioStartTime);
};

export const calculateVideoCommentaryHighlightMetadata: CalculateMetadataFunction<
  VideoCommentaryHighlightProps
> = async ({props}) => {
  const backgroundVideoSrc = resolveMediaSrc(props.backgroundVideo.url);
  const backgroundAudioSrc = resolveMediaSrc(props.backgroundAudio.url);
  const hasCompleteBackgroundVideoMeta =
    (props.backgroundVideo.width ?? 0) > 0 &&
    (props.backgroundVideo.height ?? 0) > 0 &&
    (props.backgroundVideo.fps ?? 0) > 0 &&
    (props.backgroundVideo.duration ?? 0) > 0;

  let parsedVideoMeta: Awaited<ReturnType<typeof getVideoMeta>> | null = null;
  if (!hasCompleteBackgroundVideoMeta) {
    try {
      parsedVideoMeta = await getVideoMeta(backgroundVideoSrc);
    } catch (err) {
      console.warn(
        `Failed to parse background video metadata for ${props.backgroundVideo.url}:`,
        err,
      );
    }
  }

  let parsedAudioDuration = props.backgroundAudio.duration ?? 0;
  if (parsedAudioDuration <= 0) {
    try {
      parsedAudioDuration = await getAudioDuration(backgroundAudioSrc);
    } catch (err) {
      console.warn(
        `Failed to parse background audio metadata for ${props.backgroundAudio.url}:`,
        err,
      );
    }
  }

  const explicitWidth = props.render?.width;
  const explicitHeight = props.render?.height;
  const sourceWidth = parsedVideoMeta?.width || props.backgroundVideo.width || 0;
  const sourceHeight =
    parsedVideoMeta?.height || props.backgroundVideo.height || 0;
  const width =
    explicitWidth && explicitWidth > 0
      ? explicitWidth
      : sourceWidth > 0
        ? sourceWidth
        : DEFAULT_WIDTH;
  const height =
    explicitHeight && explicitHeight > 0
      ? explicitHeight
      : sourceHeight > 0
        ? sourceHeight
        : DEFAULT_HEIGHT;
  const coordinateWidth =
    props.backgroundVideo.coordinateWidth && props.backgroundVideo.coordinateWidth > 0
      ? props.backgroundVideo.coordinateWidth
      : sourceWidth;
  const coordinateHeight =
    props.backgroundVideo.coordinateHeight &&
    props.backgroundVideo.coordinateHeight > 0
      ? props.backgroundVideo.coordinateHeight
      : sourceHeight;
  const rawFps =
    parsedVideoMeta?.fps || props.backgroundVideo.fps || DEFAULT_FPS;
  const rawVideoDuration =
    parsedVideoMeta?.duration || props.backgroundVideo.duration || 0;
  const fps = rawFps > 0 ? Math.round(rawFps) : DEFAULT_FPS;

  const sortedComments = [...props.comments].sort(
    (a, b) => a.audioStartTime - b.audioStartTime,
  );
  const totalFrames = sortedComments.reduce((frames, comment) => {
    return frames + Math.max(1, Math.ceil(getCommentAudioDuration(comment) * fps));
  }, 0);

  const fallbackVideoDuration = Math.max(
    ...sortedComments.map((comment) => comment.videoEndTime),
    ...props.highlights.map((highlight) => highlight.videoEndTime ?? 0),
    0,
  );
  const fallbackAudioDuration = Math.max(
    ...sortedComments.map((comment) => comment.audioEndTime),
    ...props.highlights.map((highlight) => highlight.audioEndTime),
    0,
  );
  const backgroundVideoDuration =
    rawVideoDuration > 0 ? rawVideoDuration : fallbackVideoDuration;
  const backgroundAudioDuration =
    parsedAudioDuration > 0 ? parsedAudioDuration : fallbackAudioDuration;

  return {
    durationInFrames: Math.max(totalFrames, 1),
    fps,
    width,
    height,
    props: {
      ...props,
      backgroundVideo: {
        ...props.backgroundVideo,
        url: backgroundVideoSrc,
        duration: backgroundVideoDuration,
        width: sourceWidth > 0 ? sourceWidth : props.backgroundVideo.width,
        height: sourceHeight > 0 ? sourceHeight : props.backgroundVideo.height,
        coordinateWidth:
          coordinateWidth > 0
            ? coordinateWidth
            : props.backgroundVideo.coordinateWidth,
        coordinateHeight:
          coordinateHeight > 0
            ? coordinateHeight
            : props.backgroundVideo.coordinateHeight,
        fps,
      },
      backgroundAudio: {
        ...props.backgroundAudio,
        url: backgroundAudioSrc,
        duration: backgroundAudioDuration,
      },
      comments: sortedComments,
    },
  };
};

interface SegmentLayout {
  from: number;
  segFrames: number;
  audioStartFrame: number;
  audioEndFrame: number;
  videoStartFrame: number;
  videoEndFrame: number;
}

interface TimedHighlight {
  audioStartFrame: number;
  audioEndFrame: number;
  videoStartFrame: number;
  videoEndFrame: number;
}

const getHighlightsForSegment = ({
  highlights,
  segment,
  fps,
}: {
  highlights: TimelineHighlightItem[];
  segment: SegmentLayout;
  fps: number;
}): TimelineHighlightItem[] => {
  return highlights.filter((highlight) => {
    const audioStartFrame = Math.round(highlight.audioStartTime * fps);
    const audioEndFrame = Math.round(highlight.audioEndTime * fps);

    return (
      audioStartFrame < segment.audioEndFrame &&
      audioEndFrame > segment.audioStartFrame
    );
  });
};

const getTimedHighlights = ({
  highlights,
  fps,
}: {
  highlights: TimelineHighlightItem[];
  fps: number;
}): TimedHighlight[] => {
  return highlights
    .filter(
      (highlight) =>
        highlight.videoStartTime != null && highlight.videoEndTime != null,
    )
    .map((highlight) => ({
      audioStartFrame: Math.round(highlight.audioStartTime * fps),
      audioEndFrame: Math.round(highlight.audioEndTime * fps),
      videoStartFrame: Math.round((highlight.videoStartTime ?? 0) * fps),
      videoEndFrame: Math.round((highlight.videoEndTime ?? 0) * fps),
    }))
    .sort((a, b) => a.audioStartFrame - b.audioStartFrame);
};

const getMappedVideoFrame = ({
  segment,
  localFrame,
  timedHighlights,
}: {
  segment: SegmentLayout;
  localFrame: number;
  timedHighlights: TimedHighlight[];
}): number => {
  const currentAudioFrame = segment.audioStartFrame + localFrame;
  let sourceFrame = segment.videoStartFrame;
  let audioCursor = segment.audioStartFrame;

  for (const highlight of timedHighlights) {
    if (highlight.audioEndFrame <= audioCursor) {
      continue;
    }

    const highlightAudioStart = Math.max(
      highlight.audioStartFrame,
      segment.audioStartFrame,
    );
    const highlightAudioEnd = Math.min(
      highlight.audioEndFrame,
      segment.audioEndFrame,
    );

    if (currentAudioFrame < highlightAudioStart) {
      const framesBeforeHighlight = currentAudioFrame - audioCursor;
      return Math.min(sourceFrame + framesBeforeHighlight, segment.videoEndFrame);
    }

    sourceFrame = Math.min(
      sourceFrame + Math.max(0, highlightAudioStart - audioCursor),
      segment.videoEndFrame,
    );

    if (currentAudioFrame < highlightAudioEnd) {
      const framesInsideHighlight = currentAudioFrame - highlightAudioStart;
      return Math.min(
        highlight.videoStartFrame + framesInsideHighlight,
        highlight.videoEndFrame,
        segment.videoEndFrame,
      );
    }

    sourceFrame = Math.min(highlight.videoEndFrame, segment.videoEndFrame);
    audioCursor = highlightAudioEnd;
  }

  return Math.min(
    sourceFrame + Math.max(0, currentAudioFrame - audioCursor),
    segment.videoEndFrame,
  );
};

interface OriginalAudioChunk {
  from: number;
  durationInFrames: number;
  startFrom: number;
}

const pushOriginalAudioChunk = ({
  chunks,
  from,
  durationInFrames,
  startFrom,
  sourceEndFrame,
}: {
  chunks: OriginalAudioChunk[];
  from: number;
  durationInFrames: number;
  startFrom: number;
  sourceEndFrame: number;
}) => {
  const playableDuration = Math.min(
    durationInFrames,
    Math.max(0, sourceEndFrame - startFrom),
  );

  if (playableDuration > 0) {
    chunks.push({
      from,
      durationInFrames: playableDuration,
      startFrom,
    });
  }
};

const getOriginalAudioChunks = ({
  segment,
  timedHighlights,
}: {
  segment: SegmentLayout;
  timedHighlights: TimedHighlight[];
}): OriginalAudioChunk[] => {
  const chunks: OriginalAudioChunk[] = [];
  let audioCursor = segment.audioStartFrame;
  let sourceFrame = segment.videoStartFrame;

  for (const highlight of timedHighlights) {
    if (highlight.audioEndFrame <= audioCursor) {
      continue;
    }

    const highlightAudioStart = Math.max(
      highlight.audioStartFrame,
      segment.audioStartFrame,
    );
    const highlightAudioEnd = Math.min(
      highlight.audioEndFrame,
      segment.audioEndFrame,
    );

    if (highlightAudioStart > audioCursor) {
      const durationInFrames = highlightAudioStart - audioCursor;
      pushOriginalAudioChunk({
        chunks,
        from: audioCursor - segment.audioStartFrame,
        durationInFrames,
        startFrom: sourceFrame,
        sourceEndFrame: segment.videoEndFrame,
      });
      sourceFrame = Math.min(
        sourceFrame + durationInFrames,
        segment.videoEndFrame,
      );
      audioCursor = highlightAudioStart;
    }

    pushOriginalAudioChunk({
      chunks,
      from: highlightAudioStart - segment.audioStartFrame,
      durationInFrames: highlightAudioEnd - highlightAudioStart,
      startFrom: highlight.videoStartFrame,
      sourceEndFrame: Math.min(highlight.videoEndFrame, segment.videoEndFrame),
    });

    sourceFrame = Math.min(highlight.videoEndFrame, segment.videoEndFrame);
    audioCursor = highlightAudioEnd;
  }

  if (audioCursor < segment.audioEndFrame) {
    pushOriginalAudioChunk({
      chunks,
      from: audioCursor - segment.audioStartFrame,
      durationInFrames: segment.audioEndFrame - audioCursor,
      startFrom: sourceFrame,
      sourceEndFrame: segment.videoEndFrame,
    });
  }

  return chunks.filter((chunk) => chunk.durationInFrames > 0);
};

const SegmentVideo: React.FC<{
  backgroundVideoUrl: string;
  segment: SegmentLayout;
  timedHighlights: TimedHighlight[];
}> = ({backgroundVideoUrl, segment, timedHighlights}) => {
  const frame = useCurrentFrame();
  const sourceFrame = getMappedVideoFrame({
    segment,
    localFrame: frame,
    timedHighlights,
  });
  const relativeFrame = Math.max(0, sourceFrame - segment.videoStartFrame);

  return (
    <Freeze frame={relativeFrame}>
      <OffthreadVideo
        src={backgroundVideoUrl}
        trimBefore={segment.videoStartFrame}
        muted
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'fill',
        }}
      />
    </Freeze>
  );
};

const toDisplayHighlights = (
  highlights: TimelineHighlightItem[],
): HighlightItem[] => {
  return highlights
    .filter((highlight) => highlight.type !== 'freeze')
    .map((highlight) => ({
      type: highlight.type,
      startTime: highlight.audioStartTime,
      endTime: highlight.audioEndTime,
      position: highlight.position,
      borderRadius: highlight.borderRadius,
      borderWidth: highlight.borderWidth,
      borderColor: highlight.borderColor,
      rotation: highlight.rotation,
      fadeIn: highlight.fadeIn,
      fadeOut: highlight.fadeOut,
      zoomScale: highlight.zoomScale,
      autoFitZoom: highlight.autoFitZoom,
      easingType: highlight.easingType,
    }));
};

const findActiveFocusArea = (
  highlights: HighlightItem[],
  currentAudioTimeInSeconds: number,
): HighlightItem | null => {
  for (let index = highlights.length - 1; index >= 0; index -= 1) {
    const highlight = highlights[index];

    if (
      highlight.type === 'focus-area' &&
      highlight.position != null &&
      isHighlightActiveAtTime(highlight, currentAudioTimeInSeconds)
    ) {
      return highlight;
    }
  }

  return null;
};

const isSegmentWaitingForAudio = ({
  segment,
  localFrame,
  timedHighlights,
}: {
  segment: SegmentLayout;
  localFrame: number;
  timedHighlights: TimedHighlight[];
}): boolean => {
  const videoFrame = getMappedVideoFrame({
    segment,
    localFrame,
    timedHighlights,
  });
  const currentAudioFrame = segment.audioStartFrame + localFrame;

  const isWaitingForHighlightAudio = timedHighlights.some((highlight) => {
    const highlightAudioStart = Math.max(
      highlight.audioStartFrame,
      segment.audioStartFrame,
    );
    const highlightAudioEnd = Math.min(
      highlight.audioEndFrame,
      segment.audioEndFrame,
    );

    if (
      currentAudioFrame < highlightAudioStart ||
      currentAudioFrame >= highlightAudioEnd
    ) {
      return false;
    }

    return videoFrame >= Math.min(highlight.videoEndFrame, segment.videoEndFrame);
  });

  return (
    isWaitingForHighlightAudio ||
    (videoFrame >= segment.videoEndFrame && localFrame < segment.segFrames - 1)
  );
};

export const VideoCommentaryHighlight: React.FC<
  VideoCommentaryHighlightProps
> = ({
  backgroundVideo,
  backgroundAudio,
  comments,
  highlights,
  voiceVolume,
  originalVolume,
  defaultFadeIn = 0,
  defaultFadeOut = 0,
}) => {
  const {fps} = useVideoConfig();
  const frame = useCurrentFrame();

  const segments: SegmentLayout[] = useMemo(() => {
    let cumFrame = 0;

    return [...comments]
      .sort((a, b) => a.audioStartTime - b.audioStartTime)
      .map((comment) => {
        const segFrames = Math.max(1, Math.ceil(getCommentAudioDuration(comment) * fps));
        const segment: SegmentLayout = {
          from: cumFrame,
          segFrames,
          audioStartFrame: Math.round(comment.audioStartTime * fps),
          audioEndFrame: Math.round(comment.audioEndTime * fps),
          videoStartFrame: Math.round(comment.videoStartTime * fps),
          videoEndFrame: Math.round(comment.videoEndTime * fps),
        };

        cumFrame += segFrames;
        return segment;
      });
  }, [comments, fps]);

  if (segments.length === 0) {
    return <AbsoluteFill style={{backgroundColor: '#000'}} />;
  }

  let currentSeg = segments[segments.length - 1];
  for (const segment of segments) {
    if (frame >= segment.from && frame < segment.from + segment.segFrames) {
      currentSeg = segment;
      break;
    }
  }

  const localFrame = frame - currentSeg.from;
  const currentAudioFrame = currentSeg.audioStartFrame + localFrame;
  const currentAudioTimeInSeconds = currentAudioFrame / fps;
  const currentSegmentHighlights = useMemo(() => {
    return getHighlightsForSegment({
      highlights,
      segment: currentSeg,
      fps,
    });
  }, [currentSeg, fps, highlights]);
  const currentTimedHighlights = useMemo(() => {
    return getTimedHighlights({
      highlights: currentSegmentHighlights,
      fps,
    });
  }, [currentSegmentHighlights, fps]);
  const displayHighlights = useMemo(() => {
    return toDisplayHighlights(currentSegmentHighlights);
  }, [currentSegmentHighlights]);
  const videoFrame = getMappedVideoFrame({
    segment: currentSeg,
    localFrame,
    timedHighlights: currentTimedHighlights,
  });
  const isWaitingForAudio = isSegmentWaitingForAudio({
    segment: currentSeg,
    localFrame,
    timedHighlights: currentTimedHighlights,
  });
  const activeFocusArea = useMemo(() => {
    return findActiveFocusArea(displayHighlights, currentAudioTimeInSeconds);
  }, [currentAudioTimeInSeconds, displayHighlights]);
  const backgroundVideoSrc = useMemo(() => {
    return resolveMediaSrc(backgroundVideo.url);
  }, [backgroundVideo.url]);
  const backgroundAudioSrc = useMemo(() => {
    return resolveMediaSrc(backgroundAudio.url);
  }, [backgroundAudio.url]);
  const totalVideoFrames = Math.max(
    1,
    Math.round((backgroundVideo.duration ?? 0) * fps),
  );
  const markerCoordinateWidth =
    backgroundVideo.coordinateWidth ?? backgroundVideo.width;
  const markerCoordinateHeight =
    backgroundVideo.coordinateHeight ?? backgroundVideo.height;
  const progress = Math.min(
    1,
    Math.max(0, videoFrame / Math.max(totalVideoFrames - 1, 1)),
  );

  return (
    <AbsoluteFill style={{backgroundColor: '#000'}}>
      <FocusAreaMarker
        highlight={activeFocusArea}
        defaultFadeIn={defaultFadeIn}
        defaultFadeOut={defaultFadeOut}
        currentTimeInSeconds={currentAudioTimeInSeconds}
        coordinateWidth={markerCoordinateWidth}
        coordinateHeight={markerCoordinateHeight}
      >
        {segments.map((segment, index) => (
          <Sequence
            key={`video-${index}`}
            from={segment.from}
            durationInFrames={segment.segFrames}
          >
            <SegmentVideo
              backgroundVideoUrl={backgroundVideoSrc}
              segment={segment}
              timedHighlights={getTimedHighlights({
                highlights: getHighlightsForSegment({
                  highlights,
                  segment,
                  fps,
                }),
                fps,
              })}
            />
          </Sequence>
        ))}
      </FocusAreaMarker>

      <HighlightOverlay
        highlights={displayHighlights}
        defaultFadeIn={defaultFadeIn}
        defaultFadeOut={defaultFadeOut}
        currentTimeInSeconds={currentAudioTimeInSeconds}
        coordinateWidth={markerCoordinateWidth}
        coordinateHeight={markerCoordinateHeight}
      />

      <div
        style={{
          position: 'absolute',
          bottom: 24,
          left: 24,
          right: 24,
          height: 8,
          zIndex: 10,
        }}
      >
        <div
          style={{
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(255, 255, 255, 0.28)',
            borderRadius: 999,
            overflow: 'hidden',
            boxShadow: '0 0 0 1px rgba(0,0,0,0.35)',
          }}
        >
          <div
            style={{
              height: '100%',
              width: `${progress * 100}%`,
              background: 'linear-gradient(90deg, #4fc3f7, #0288d1)',
              borderRadius: 999,
            }}
          />
        </div>

        {isWaitingForAudio ? (
          <div
            style={{
              position: 'absolute',
              left: `${progress * 100}%`,
              top: '50%',
              transform: 'translate(-50%, -50%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 22,
              height: 22,
              borderRadius: '50%',
              background: 'rgba(2, 136, 209, 0.92)',
              boxShadow:
                '0 0 8px rgba(79, 195, 247, 0.6), 0 0 16px rgba(2, 136, 209, 0.35)',
              border: '2px solid rgba(255, 255, 255, 0.85)',
            }}
          >
            <div style={{display: 'flex', gap: 2}}>
              <div
                style={{
                  width: 3,
                  height: 9,
                  borderRadius: 1,
                  backgroundColor: '#fff',
                }}
              />
              <div
                style={{
                  width: 3,
                  height: 9,
                  borderRadius: 1,
                  backgroundColor: '#fff',
                }}
              />
            </div>
          </div>
        ) : null}
      </div>

      {segments.map((segment, index) => {
        const timedHighlights = getTimedHighlights({
          highlights: getHighlightsForSegment({
            highlights,
            segment,
            fps,
          }),
          fps,
        });
        const originalAudioChunks = getOriginalAudioChunks({
          segment,
          timedHighlights,
        });

        return (
          <Sequence
            key={`audio-${index}`}
            from={segment.from}
            durationInFrames={segment.segFrames}
          >
            {originalVolume > 0 ? (
              <>
                {originalAudioChunks.map((chunk, chunkIndex) => (
                  <Sequence
                    key={`original-audio-${index}-${chunkIndex}`}
                    from={chunk.from}
                    durationInFrames={chunk.durationInFrames}
                  >
                    <Audio
                      src={backgroundVideoSrc}
                      startFrom={chunk.startFrom}
                      volume={originalVolume / 100}
                    />
                  </Sequence>
                ))}
              </>
            ) : null}
            <Audio
              src={backgroundAudioSrc}
              startFrom={segment.audioStartFrame}
              volume={voiceVolume / 100}
            />
          </Sequence>
        );
      })}
    </AbsoluteFill>
  );
};
